import React, { useMemo, useState } from 'react';
import { Modal, View, Text, StyleSheet, ScrollView, Image, Pressable } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { collectStudentSummary, SessionSummaryData } from './utils';

interface Props {
    visible: boolean;
    onClose: () => void;
    sessionDays: string[];
    sessionSummary: SessionSummaryData;
    userId?: string;
}

const SummaryModal: React.FC<Props> = ({ visible, onClose, sessionDays, sessionSummary, userId }) => {
    const { comments, photos } = useMemo(() => collectStudentSummary(sessionDays, sessionSummary, userId), [sessionDays, sessionSummary, userId]);
    const [selectedPhoto, setSelectedPhoto] = useState<any>(null);

    const formatDateTime = (timestamp: string) => {
        const date = new Date(timestamp);
        const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        return time;
    };

    const formatDate = (dateStr: string) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    return (
        <Modal visible={visible} animationType="slide" transparent={true}>
            <View style={styles.modalOverlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <View style={styles.headerLeft}>
                            <View style={styles.iconContainer}>
                                <MaterialCommunityIcons name="star-circle" size={28} color="#2C67D6" />
                            </View>
                            <View>
                                <Text style={styles.modalTitle}>Session Summary</Text>
                                <Text style={styles.modalSubtitle}>
                                    {comments.length} {comments.length === 1 ? 'comment' : 'comments'} • {photos.length} {photos.length === 1 ? 'moment' : 'moments'}
                                </Text>
                            </View>
                        </View>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={24} color="#666" />
                        </Pressable>
                    </View>

                    <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
                        {/* Comments Section */}
                        <View style={styles.section}>
                            <View style={styles.sectionHeader}>
                                <View style={styles.sectionIconBadge}>
                                    <MaterialCommunityIcons name="comment-text" size={20} color="#2C67D6" />
                                </View>
                                <Text style={styles.sectionTitle}>Comments</Text>
                                <View style={styles.countBadge}>
                                    <Text style={styles.countBadgeText}>{comments.length}</Text>
                                </View>
                            </View>

                            {(!comments || comments.length === 0) ? (
                                <View style={styles.emptyState}>
                                    <MaterialCommunityIcons name="comment-outline" size={48} color="#E0E0E0" />
                                    <Text style={styles.emptyText}>No comments yet</Text>
                                    <Text style={styles.emptySubtext}>Comments from the coach will appear here</Text>
                                </View>
                            ) : (
                                <View style={styles.commentsContainer}>
                                    {comments.map((c: any, idx: number) => (
                                        <View key={`${c.id || 'c'}_${idx}`} style={styles.commentCard}>
                                            <View style={styles.commentHeader}>
                                                <View style={styles.authorBadge}>
                                                    <MaterialCommunityIcons 
                                                        name={c.author === 'Coach' ? 'whistle' : 'account-circle'} 
                                                        size={16} 
                                                        color={c.author === 'Coach' ? '#2C67D6' : '#666'} 
                                                    />
                                                    <Text style={[styles.authorText, c.author === 'Coach' && styles.coachText]}>
                                                        {c.author}
                                                    </Text>
                                                </View>
                                                <View style={styles.dateTimeBadge}>
                                                    <MaterialCommunityIcons name="calendar" size={12} color="#999" />
                                                    <Text style={styles.dateText}>{formatDate(c._date)}</Text>
                                                    <MaterialCommunityIcons name="clock-outline" size={12} color="#999" />
                                                    <Text style={styles.timeText}>{formatDateTime(c.timestamp)}</Text>
                                                </View>
                                            </View>
                                            <Text style={styles.commentText}>{c.text}</Text>
                                        </View>
                                    ))}
                                </View>
                            )}
                        </View>

                        {/* Moments Section */}
                        <View style={styles.section}>
                            <View style={styles.sectionHeader}>
                                <View style={styles.sectionIconBadge}>
                                    <MaterialCommunityIcons name="camera" size={20} color="#FF6B9D" />
                                </View>
                                <Text style={styles.sectionTitle}>Moments</Text>
                                <View style={[styles.countBadge, { backgroundColor: '#FFE5F0' }]}>
                                    <Text style={[styles.countBadgeText, { color: '#FF6B9D' }]}>{photos.length}</Text>
                                </View>
                            </View>

                            {(!photos || photos.length === 0) ? (
                                <View style={styles.emptyState}>
                                    <MaterialCommunityIcons name="camera-outline" size={48} color="#E0E0E0" />
                                    <Text style={styles.emptyText}>No moments captured yet</Text>
                                    <Text style={styles.emptySubtext}>Photos will appear here as they're added</Text>
                                </View>
                            ) : (
                                <View style={styles.photosGrid}>
                                    {photos.map((p: any, idx: number) => (
                                        <Pressable 
                                            key={`${p.id || 'p'}_${idx}`} 
                                            style={styles.photoCard}
                                            onPress={() => setSelectedPhoto(p)}
                                        >
                                            <Image source={{ uri: p.uri }} style={styles.photoImage} resizeMode="cover" />
                                            <View style={styles.photoOverlay}>
                                                <MaterialCommunityIcons name="fullscreen" size={20} color="white" />
                                            </View>
                                            <View style={styles.photoFooter}>
                                                <View style={styles.photoDateBadge}>
                                                    <MaterialCommunityIcons name="calendar" size={12} color="#666" />
                                                    <Text style={styles.photoDateText}>{formatDate(p._date)}</Text>
                                                </View>
                                                {p.caption && (
                                                    <Text style={styles.photoCaptionPreview} numberOfLines={1}>
                                                        {p.caption}
                                                    </Text>
                                                )}
                                            </View>
                                        </Pressable>
                                    ))}
                                </View>
                            )}
                        </View>
                    </ScrollView>
                </View>
            </View>

            {/* Photo Viewer Modal */}
            {selectedPhoto && (
                <Modal visible={true} animationType="fade" transparent={true}>
                    <View style={styles.photoViewerOverlay}>
                        <Pressable style={styles.photoViewerClose} onPress={() => setSelectedPhoto(null)}>
                            <MaterialCommunityIcons name="close-circle" size={40} color="white" />
                        </Pressable>
                        <Image 
                            source={{ uri: selectedPhoto.uri }} 
                            style={styles.photoViewerImage} 
                            resizeMode="contain" 
                        />
                        <View style={styles.photoViewerInfo}>
                            <Text style={styles.photoViewerDate}>
                                {formatDate(selectedPhoto._date)}
                            </Text>
                            {selectedPhoto.caption && (
                                <Text style={styles.photoViewerCaption}>{selectedPhoto.caption}</Text>
                            )}
                        </View>
                    </View>
                </Modal>
            )}
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContainer: {
        width: '90%',
        maxWidth: 900,
        height: '85%',
        backgroundColor: 'white',
        borderRadius: 24,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
        elevation: 15,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 24,
        borderBottomWidth: 1,
        borderBottomColor: '#E8E8E8',
        backgroundColor: '#FAFBFC',
    },
    headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    iconContainer: {
        marginRight: 12,
    },
    modalTitle: {
        fontSize: 22,
        fontWeight: '700',
        color: '#1A1A1A',
        marginBottom: 2,
    },
    modalSubtitle: {
        fontSize: 13,
        color: '#666',
        fontWeight: '500',
    },
    closeButton: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: '#F0F0F0',
        alignItems: 'center',
        justifyContent: 'center',
    },
    content: {
        flex: 1,
        backgroundColor: '#F8F9FA',
    },
    section: {
        padding: 20,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    sectionIconBadge: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: '#E3F2FD',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 10,
    },
    sectionTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: '#1A1A1A',
        flex: 1,
    },
    countBadge: {
        backgroundColor: '#E3F2FD',
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 12,
    },
    countBadgeText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#2C67D6',
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 48,
        paddingHorizontal: 24,
        backgroundColor: 'white',
        borderRadius: 16,
        borderWidth: 2,
        borderColor: '#F0F0F0',
        borderStyle: 'dashed',
    },
    emptyText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#999',
        marginTop: 12,
    },
    emptySubtext: {
        fontSize: 14,
        color: '#BBB',
        marginTop: 4,
        textAlign: 'center',
    },
    commentsContainer: {
        gap: 12,
    },
    commentCard: {
        backgroundColor: 'white',
        borderRadius: 16,
        padding: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
        borderWidth: 1,
        borderColor: '#F0F0F0',
    },
    commentHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    authorBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F5F5F5',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 8,
    },
    authorText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#666',
        marginLeft: 6,
    },
    coachText: {
        color: '#2C67D6',
    },
    dateTimeBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    dateText: {
        fontSize: 12,
        color: '#999',
        fontWeight: '500',
    },
    timeText: {
        fontSize: 12,
        color: '#999',
        fontWeight: '500',
    },
    commentText: {
        fontSize: 15,
        color: '#333',
        lineHeight: 22,
    },
    photosGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
    photoCard: {
        width: '48%',
        backgroundColor: 'white',
        borderRadius: 16,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
        elevation: 3,
        borderWidth: 1,
        borderColor: '#F0F0F0',
    },
    photoImage: {
        width: '100%',
        height: 200,
        backgroundColor: '#F0F0F0',
    },
    photoOverlay: {
        position: 'absolute',
        top: 12,
        right: 12,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
    },
    photoFooter: {
        padding: 12,
    },
    photoDateBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginBottom: 6,
    },
    photoDateText: {
        fontSize: 12,
        color: '#666',
        fontWeight: '600',
    },
    photoCaptionPreview: {
        fontSize: 13,
        color: '#666',
        lineHeight: 18,
    },
    // Photo Viewer
    photoViewerOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    photoViewerClose: {
        position: 'absolute',
        top: 50,
        right: 20,
        zIndex: 10,
    },
    photoViewerImage: {
        width: '90%',
        height: '70%',
    },
    photoViewerInfo: {
        position: 'absolute',
        bottom: 40,
        left: 20,
        right: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: 16,
        borderRadius: 12,
    },
    photoViewerDate: {
        fontSize: 14,
        color: 'white',
        fontWeight: '600',
        marginBottom: 4,
    },
    photoViewerCaption: {
        fontSize: 15,
        color: 'white',
        lineHeight: 22,
    },
});

export default SummaryModal;


